// Translation completeness — pure utility for the questionnaire detail page.
//
// A "translatable field" is a value object whose keys are exactly a subset
// of the four supported language codes (en, de, fr, it). The codebase
// already uses this shape consistently (see _data/cards.js isLangMap).
// Anything else in a card's JSONB tree — codes, slugs, scores, ids,
// arbitrary nested config — is ignored by the walker.
//
// Optional-field rule (important):
//
//   A translatable field is counted as a translation target ONLY when at
//   least one of its locale values is non-empty. If the author left a
//   field empty in every language, we treat it as intentionally unused
//   and skip it entirely — neither filled nor missing.
//
//   This stops optional fields like `prompt`, `subtitle`, `tooltip_body`,
//   `help_text` from dragging the percentage down when an author chose
//   not to use them.
//
// Vacuous 100%:
//
//   If the questionnaire has zero in-use translatable fields, every locale
//   reports 100%. Avoids the "0% complete" misreading for a brand-new
//   empty questionnaire or a card that legitimately has no text.

export const LOCALES = ['en', 'de', 'fr', 'it'];

function isLangMap(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const keys = Object.keys(obj);
  if (keys.length === 0) return false;
  return keys.every(k => LOCALES.includes(k));
}

function isLocaleFilled(langMap, locale) {
  const v = langMap[locale];
  return typeof v === 'string' && v.trim().length > 0;
}

function isLangMapInUse(langMap) {
  for (const loc of LOCALES) {
    if (isLocaleFilled(langMap, loc)) return true;
  }
  return false;
}

// Walk an arbitrary JSON tree and yield each *in-use* lang map. Empty
// lang maps are skipped per the optional-field rule above.
function* walkInUseLangMaps(value) {
  if (isLangMap(value)) {
    if (isLangMapInUse(value)) yield value;
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) yield* walkInUseLangMaps(item);
    return;
  }
  if (value && typeof value === 'object') {
    for (const k of Object.keys(value)) yield* walkInUseLangMaps(value[k]);
  }
}

/**
 * Compute per-locale completeness percentages for a questionnaire.
 *
 * @param {Object} input
 * @param {Array}  input.sections - section objects with `.name` (a lang map)
 * @param {Array}  input.cards    - legacy seq_cards rows with `.content` trees
 *                                  (dormant post-junction-Step-2; kept so
 *                                   callers that haven't migrated still work)
 * @param {Array}  input.items    - junction items; each has `.item`, the
 *                                  inlined source row. Walked alongside
 *                                  sections — the same generic lang-map
 *                                  walker handles every item_type without
 *                                  per-type logic.
 * @returns {{en: number, de: number, fr: number, it: number}} whole-number
 *   percentages 0-100. Returns 100 for every locale when there are no
 *   in-use translatable fields anywhere.
 */
export function computeCompleteness({ sections = [], cards = [], items = [] } = {}) {
  const filled = { en: 0, de: 0, fr: 0, it: 0 };
  let total = 0;

  function tally(tree) {
    for (const lm of walkInUseLangMaps(tree)) {
      total += 1;
      for (const loc of LOCALES) {
        if (isLocaleFilled(lm, loc)) filled[loc] += 1;
      }
    }
  }

  for (const s of sections) tally(s && s.name);
  for (const c of cards) tally(c && c.content);
  for (const it of items) tally(it && it.item);

  if (total === 0) {
    return { en: 100, de: 100, fr: 100, it: 100 };
  }

  const out = {};
  for (const loc of LOCALES) {
    out[loc] = Math.round((filled[loc] / total) * 100);
  }
  return out;
}

/**
 * True iff this card has at least one in-use translatable field that is
 * empty in the given locale. Used by the filter to decide which cards
 * to show.
 */
export function cardHasMissingTranslations(card, locale) {
  if (!card) return false;
  for (const lm of walkInUseLangMaps(card.content)) {
    if (!isLocaleFilled(lm, locale)) return true;
  }
  return false;
}

/**
 * True iff this section's name is in-use but missing the given locale.
 * Sections-with-missing-names are surfaced alongside cards by the filter
 * so the user can find them too.
 */
export function sectionNameHasMissingTranslation(section, locale) {
  if (!section || !section.name) return false;
  if (!isLangMap(section.name)) return false;
  if (!isLangMapInUse(section.name)) return false;
  return !isLocaleFilled(section.name, locale);
}

/**
 * Junction-aware version of cardHasMissingTranslations. Walks the
 * inlined source row at `item.item` (question content, content_card
 * blocks, or flow_card name) and returns true if any in-use lang map
 * lacks the given locale.
 *
 * The same generic walker handles all three item_type variants — no
 * per-type logic is needed because all translatable text in every
 * shape lives in lang maps the walker already recognises.
 */
export function itemHasMissingTranslations(item, locale) {
  if (!item || !item.item) return false;
  for (const lm of walkInUseLangMaps(item.item)) {
    if (!isLocaleFilled(lm, locale)) return true;
  }
  return false;
}
